import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { eventService } from '../services/eventService';
import { EventResponse } from '../types';
import { notifyVisit } from '../services/notifyService';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
  min-height: 100vh;
  background: #0f0f23;
  color: #e2e8f0;
`;

const Topbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 2rem;
  background: #1a1a35;
  border-bottom: 1px solid #2d2d5e;
`;

const Logo = styled.span`
  font-size: 1.3rem;
  font-weight: 800;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Avatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`;

const LogoutBtn = styled.button`
  background: transparent;
  border: 1px solid #2d2d5e;
  color: #94a3b8;
  padding: 0.4rem 0.9rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
  &:hover { border-color: #7c3aed; color: #e2e8f0; }
`;

const Content = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0 0 0.5rem;
`;

const PageSub = styled.p`
  color: #94a3b8;
  margin: 0 0 2rem;
`;

const TopActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const NewEventBtn = styled(Link)`
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 700;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
`;

const EventGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.25rem;
`;

const EventCard = styled(Link)`
  background: #1a1a35;
  border: 1px solid #2d2d5e;
  border-radius: 16px;
  padding: 1.5rem;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.2s, transform 0.2s;
  display: block;
  &:hover { border-color: #7c3aed; transform: translateY(-2px); }
`;

const EventName = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
  color: #e2e8f0;
`;

const EventMeta = styled.p`
  color: #94a3b8;
  font-size: 0.85rem;
  margin: 0 0 1rem;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  background: ${p => {
    if (p.status === 'ACTIVE') return 'rgba(16,185,129,0.15)';
    if (p.status === 'EXPIRED') return 'rgba(239,68,68,0.15)';
    return 'rgba(148,163,184,0.15)';
  }};
  color: ${p => {
    if (p.status === 'ACTIVE') return '#10b981';
    if (p.status === 'EXPIRED') return '#ef4444';
    return '#94a3b8';
  }};
`;

const PhotoCount = styled.div`
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #94a3b8;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #94a3b8;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const DashboardPage: React.FC = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState<EventResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    notifyVisit('admin', `Dashboard — ${admin?.email || 'admin'}`);
    eventService.listEvents()
      .then(setEvents)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  const activeEvents = events.filter(e => e.status === 'ACTIVE');
  const pastEvents = events.filter(e => e.status !== 'ACTIVE');

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString(i18n.language === 'es' ? 'es-MX' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <Wrapper>
      <Topbar>
        <Logo>FotoVibe</Logo>
        <UserInfo>
          {admin?.picture && <Avatar src={admin.picture} alt={admin.name} />}
          <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>{admin?.name}</span>
          <LogoutBtn onClick={handleLogout}>{t('dashboard.logout')}</LogoutBtn>
        </UserInfo>
      </Topbar>

      <Content>
        <TopActions>
          <div>
            <PageTitle>{t('dashboard.title')}</PageTitle>
            <PageSub>{t('dashboard.sub')}</PageSub>
          </div>
          <NewEventBtn to="/dashboard/nuevo-evento">{t('dashboard.new_event')}</NewEventBtn>
        </TopActions>

        {loading && <p style={{ color: '#94a3b8' }}>{t('dashboard.loading')}</p>}

        {!loading && events.length === 0 && (
          <EmptyState>
            <EmptyIcon>📸</EmptyIcon>
            <h3 style={{ color: '#e2e8f0', marginBottom: '0.5rem' }}>{t('dashboard.empty_title')}</h3>
            <p>{t('dashboard.empty_sub')}</p>
            <NewEventBtn to="/dashboard/nuevo-evento" style={{ display: 'inline-block', marginTop: '1rem' }}>
              {t('dashboard.empty_btn')}
            </NewEventBtn>
          </EmptyState>
        )}

        {activeEvents.length > 0 && (
          <>
            <h2 style={{ fontSize: '1.1rem', color: '#94a3b8', marginBottom: '1rem' }}>{t('dashboard.active_section')}</h2>
            <EventGrid>
              {activeEvents.map(event => (
                <EventCard key={event.id} to={`/dashboard/evento/${event.id}`}>
                  <EventName>{event.name}</EventName>
                  <EventMeta>
                    {t('dashboard.event_date')} {event.eventDate ? formatDate(event.eventDate) : t('dashboard.no_date')}
                  </EventMeta>
                  <StatusBadge status={event.status}>
                    {event.status === 'ACTIVE' ? t('dashboard.status_active') : event.status}
                  </StatusBadge>
                  <PhotoCount>{event.photoCount ?? 0} {t('dashboard.photos_approved')}</PhotoCount>
                  {event.ownerName && (
                    <p style={{ fontSize: '0.8rem', color: '#a78bfa', marginTop: '0.5rem', marginBottom: 0 }}>
                      {t('dashboard.owner')} {event.ownerName}
                    </p>
                  )}
                  {event.ownerEmail && (
                    <p style={{ fontSize: '0.8rem', color: '#a78bfa', marginTop: '0.15rem', marginBottom: 0 }}>
                      {event.ownerEmail}
                    </p>
                  )}
                  {event.expiresAt && (
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem', marginBottom: 0 }}>
                      {t('dashboard.expires')} {new Date(event.expiresAt).toLocaleDateString(i18n.language === 'es' ? 'es-MX' : 'en-US')}
                    </p>
                  )}
                  {event.createdAt && (
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem', marginBottom: 0 }}>
                      {t('dashboard.created')} {new Date(event.createdAt).toLocaleDateString(i18n.language === 'es' ? 'es-MX' : 'en-US')}
                    </p>
                  )}
                </EventCard>
              ))}
            </EventGrid>
          </>
        )}

        {pastEvents.length > 0 && (
          <>
            <h2 style={{ fontSize: '1.1rem', color: '#94a3b8', margin: '2rem 0 1rem' }}>{t('dashboard.past_section')}</h2>
            <EventGrid>
              {pastEvents.map(event => (
                <EventCard key={event.id} to={`/dashboard/evento/${event.id}`}>
                  <EventName style={{ opacity: 0.7 }}>{event.name}</EventName>
                  <EventMeta>
                    {t('dashboard.event_date')} {event.eventDate ? new Date(event.eventDate).toLocaleDateString(i18n.language === 'es' ? 'es-MX' : 'en-US') : t('dashboard.no_date')}
                  </EventMeta>
                  <StatusBadge status={event.status}>
                    {event.status === 'EXPIRED' ? t('dashboard.status_expired') : event.status}
                  </StatusBadge>
                  <PhotoCount>{event.photoCount ?? 0} {t('dashboard.photos')}</PhotoCount>
                  {event.ownerName && (
                    <p style={{ fontSize: '0.8rem', color: '#a78bfa', marginTop: '0.5rem', marginBottom: 0 }}>
                      {t('dashboard.owner')} {event.ownerName}
                    </p>
                  )}
                  {event.ownerEmail && (
                    <p style={{ fontSize: '0.8rem', color: '#a78bfa', marginTop: '0.15rem', marginBottom: 0 }}>
                      {event.ownerEmail}
                    </p>
                  )}
                  {event.createdAt && (
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem', marginBottom: 0 }}>
                      {t('dashboard.created')} {new Date(event.createdAt).toLocaleDateString(i18n.language === 'es' ? 'es-MX' : 'en-US')}
                    </p>
                  )}
                </EventCard>
              ))}
            </EventGrid>
          </>
        )}
      </Content>
    </Wrapper>
  );
};

export default DashboardPage;
